import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Alert, Button, Card, Drawer, Space, Table, Tooltip, Typography, message } from 'antd';
import { CloudDownloadOutlined, ReloadOutlined, DownloadOutlined } from '@ant-design/icons';
import { ingestionStore } from '../store/IngestionStore';
import { ingestionApi } from '../ingestionApi';

const { Text, Paragraph } = Typography;

const shortId = (id) => (id ? `${id.slice(0, 8)}…` : '—');

export const Step2Scrape = observer(({ item }) => {
    const [rawOpen, setRawOpen] = useState(false);
    const [raw, setRaw] = useState(null);
    const [rawVariantId, setRawVariantId] = useState(null);
    const [rawLoading, setRawLoading] = useState(false);

    useEffect(() => {
        ingestionStore.loadScrapeSummary(item.id);
    }, [item.id]);

    // While the worker is scraping, poll for progress. Use the silent refresh so
    // the detail panel doesn't flash to a spinner (and unmount this component)
    // on every tick.
    useEffect(() => {
        if (item.status !== 'running') return undefined;
        const interval = setInterval(() => {
            ingestionStore.refreshCurrentItem(item.id);
            ingestionStore.loadScrapeSummary(item.id);
        }, 2500);
        return () => clearInterval(interval);
    }, [item.status, item.id]);

    const handleRunScrape = async () => {
        try {
            await ingestionStore.runStep(item.id, 2);
            message.success('Scrape queued');
            await ingestionStore.refreshCurrentItem(item.id);
            await ingestionStore.loadScrapeSummary(item.id);
        } catch (e) {
            message.error(e.message || 'Failed to start scrape');
        }
    };

    const openRaw = async (variantId) => {
        setRawOpen(true);
        setRawLoading(true);
        setRaw(null);
        setRawVariantId(variantId);
        try {
            const data = await ingestionApi.getVariantRaw(item.id, variantId);
            setRaw(data);
        } catch (e) {
            setRaw({ error: e.message || 'No data' });
        } finally {
            setRawLoading(false);
        }
    };

    const downloadRaw = () => {
        const blob = new Blob([JSON.stringify(raw, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `scrape-${rawVariantId || item.id}.json`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const columns = [
        { title: 'Variant', dataIndex: 'variantId', key: 'variantId', render: shortId },
        {
            title: 'Purchase URL',
            dataIndex: 'purchaseUrl',
            key: 'purchaseUrl',
            ellipsis: true,
            render: (url) => url || <Text type="secondary">—</Text>,
        },
        {
            title: 'Fetched',
            dataIndex: 'fetchedAt',
            key: 'fetchedAt',
            render: (v) => (v ? new Date(v).toLocaleString() : '—'),
        },
        {
            title: '',
            key: 'actions',
            render: (_, row) =>
                row.hasData ? (
                    <Button size="small" onClick={() => openRaw(row.variantId)}>
                        View raw
                    </Button>
                ) : null,
        },
    ];

    return (
        <Card>
            <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
                <Text type="secondary">
                    Variants with a purchase URL are scraped via brightdata.
                </Text>
                <Space>
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={() => {
                            ingestionStore.refreshCurrentItem(item.id);
                            ingestionStore.loadScrapeSummary(item.id);
                        }}
                        loading={ingestionStore.loadingScrape}
                    >
                        Refresh
                    </Button>
                    <Tooltip title="Queues the scrape job for this item (runs in the worker)">
                        <Button
                            type="primary"
                            icon={<CloudDownloadOutlined />}
                            loading={ingestionStore.runningStep}
                            onClick={handleRunScrape}
                        >
                            Run scrape
                        </Button>
                    </Tooltip>
                </Space>
            </Space>

            {item.status === 'failed_to_fetch' && (
                <Alert
                    type="error"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="Scrape failed after retries"
                    description="The item was switched to manual mode. Fix the URLs or fill the data manually in step 3."
                />
            )}

            <Table
                rowKey="variantId"
                size="small"
                loading={ingestionStore.loadingScrape}
                columns={columns}
                dataSource={ingestionStore.scrapeSummary.slice()}
                pagination={false}
            />

            <Drawer
                title="Scraped data"
                width={680}
                open={rawOpen}
                onClose={() => setRawOpen(false)}
                extra={
                    <Button
                        icon={<DownloadOutlined />}
                        disabled={!raw || rawLoading}
                        onClick={downloadRaw}
                    >
                        Download full data
                    </Button>
                }
            >
                {rawLoading ? (
                    <Text type="secondary">Loading…</Text>
                ) : (
                    <Paragraph>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            HTML truncated below for display — use “Download full data” for the complete payload.
                        </Text>
                        <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: '70vh', overflow: 'auto' }}>
                            {raw ? JSON.stringify({ ...raw, html: raw.html ? `${raw.html.slice(0, 5000)}…` : raw.html }, null, 2) : ''}
                        </pre>
                    </Paragraph>
                )}
            </Drawer>
        </Card>
    );
});
